
import React, { useRef, useEffect } from 'react'
import { NODE_VISUAL_CONSTANTS } from '@genieartinc/schemas'

interface PromptTextareaProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  className?: string
  onExecute?: () => void
  disabled?: boolean
  selected?: boolean
}

export function PromptTextarea({
  value,
  onChange,
  placeholder = 'Enter your prompt... (Ctrl+Enter to execute)',
  className = '',
  onExecute,
  disabled,
  selected = false,
}: PromptTextareaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Auto-resize textarea based on content
  const resizeTextarea = () => {
    const textarea = textareaRef.current
    if (!textarea) return

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto'

    // Calculate new height using schema constants
    const { minHeight, maxHeight } = NODE_VISUAL_CONSTANTS.textarea
    const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight)
    textarea.style.height = `${newHeight}px`
  }

  // Resize on value change
  useEffect(() => {
    resizeTextarea()
  }, [value])

  // Initial resize
  useEffect(() => {
    resizeTextarea()
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Ctrl+Enter or Cmd+Enter → Execute
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && onExecute && !disabled) {
      e.preventDefault()
      onExecute()
    }
  }

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      spellCheck={false}
      className={`w-full resize-none border-0 bg-transparent pt-1 pl-1 text-sm outline-none placeholder:text-muted-foreground overflow-y-auto scrollbar-thin ${className} ${
        disabled ? 'cursor-not-allowed opacity-50' : ''
      }`}
      style={{
        minHeight: `${NODE_VISUAL_CONSTANTS.textarea.minHeight}px`,
        maxHeight: `${NODE_VISUAL_CONSTANTS.textarea.maxHeight}px`,
      }}
      onClick={(e) => {
        // Only stop propagation if already selected (to prevent drag while typing)
        if (selected) {
          e.stopPropagation()
        }
      }}
    />
  )
}
